import React, { useState, useMemo, createRef } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { convertToSlug } from "utils/convertToSlug";

import Overview from "components/program/tabs/overview";
import WhatsItLike from "components/program/tabs/whatsitlike";
import OurEthics from "components/program/tabs/ourethics";
import YourImpact from "components/program/tabs/yourimpact";
import SupportSafety from "components/program/tabs/supportsafety";
import ParentInfo from "components/program/tabs/parentInfo";
import WhatsIncluded from "components/program/tabs/whatsIncluded";
import Itinerary from "components/program/tabs/itinerary";
import ProgramEthics from "components/program/tabs/programEthics";

import Testimonials from "components/program/tabs/testimonials";
import { DesktopTabletOnly, MobileOnly } from "../base/wrapper/device";
import GroupProgramAccordion from "./GroupProgramAccordion";
import GroupProgramViewTabs from "./GroupProgramViewTabs";

const GroupProgramTabs = ({
  programDetails,
  programLocation,
  sharedDetails,
  program,
  programHub,
  setAlumniOpenModal,
  location,
  testimonials,
  setItineraryOpenModal,
  itineraryOpenModal,
  getActiveTabSlidebySlug,
  setActiveTabSlidebySlug
}) => {
  const { genericDetails } = useStaticQuery(graphql`
    query {
      genericDetails: wp {
        ...WpSharedProgramFragment
        globalProgramConfig: gviConfigSettings {
          acfGenericGlobalConfig {
            meetTheTeamGetAcquaintedText
            meetTheTeamDesc
            pgmActivitySectionDesc
            pgmActivitySectionTitle
            pgmHighlightSectionDesc
            pgmHighlightSectionTitle
            ...AlumniGalleryFragment
            ...ExclusiveOffersFragment
            ...StickyWidgetFragment
          }
        }
      }
    }
  `);

  const { taxonomyGroupProgramTypes, acfProgramsMeta } = program ?? {};

  const {
    whatItsLike,
    ourEthics,
    yourImpact,
    supportAndSafety,
    itinerary,
    whatsIncluded,
    globalProgramConfig,
    parentInfo,
    programEthics
  } = genericDetails ?? {};

  const whatitslike = whatItsLike?.acfWhatItsLike ?? "";

  const ourEthicsDetails = ourEthics?.ourEthics ?? "";

  const yourImpactDetails = yourImpact?.yourImpact ?? "";
  const locationPages = programHub?.slug ?? "";

  const supportSafety = supportAndSafety?.supportAndSafety ?? "";
  const parentInfoDetail = parentInfo?.parentInfo ?? "";

  const whatsIncludedDetail = whatsIncluded?.whatsIncluded ?? [];

  const coreGroupsInclusions = whatsIncludedDetail?.groupsIncludesGeneral ?? [];

  const coreGroupsExclusions = whatsIncludedDetail?.groupsExclusions ?? [];

  const programEthicsDetails =
    programEthics?.programEthics?.programEthics ?? [];

  const groupProgramTypes = taxonomyGroupProgramTypes?.nodes?.map(
    (item) => item.slug
  );

  const groupExpeditions = [];

  const groupExpExpeditionsMainTitle = "";

  const groupExpeditionsMainDescription = "";

  const itineraryDetails = itinerary?.itinerary || [];

  //filter the collection to the program location & product type
  const itineraryCollection = itineraryDetails?.itineraryCollection?.filter(
    (item) => {
      return (
        item?.gitcProductType?.some((type) =>
          groupProgramTypes?.includes(type.slug)
        ) &&
        item?.gitcGeographical?.some((geo) => locationPages?.includes(geo.slug))
      );
    }
  );

  const sharedItineraryList =
    itineraryCollection?.flatMap((itineraryCol) => {
      return itineraryCol?.gitcItineraryItems?.map((itineraryItem) => {
        return {
          itineraryDay: itineraryItem?.gitcItineraryItemDay,
          itineraryDescription: itineraryItem?.gitcItineraryItemDescription,
          itineraryImage: itineraryItem?.gitcItineraryItemImage,
          itineraryWeekday: itineraryItem?.gitcItineraryItemWeekday
        };
      });
    }) || [];

  const itineraryList =
    acfProgramsMeta?.programItinerary ?? sharedItineraryList ?? [];

  const programItineraryFile =
    acfProgramsMeta?.programItineraryFile?.mediaItemUrl ||
    (itineraryCollection?.length > 0
      ? itineraryCollection[0]?.gitcItineraryFile?.mediaItemUrl
      : null) ||
    null;

  const tabsDetails = [
    {
      genTabTitle: "Overview",
      tabComponent: (
        <Overview
          projectOverview={acfProgramsMeta?.projectOverview}
          programFocus={programDetails?.programFocuses}
          programTypes={taxonomyGroupProgramTypes}
          programLocation={programLocation}
          groupExpeditions={groupExpeditions}
          groupExpeditionsMainDescription={groupExpeditionsMainDescription}
          groupExpExpeditionsMainTitle={groupExpExpeditionsMainTitle}
        />
      )
    },

    itineraryDetails &&
      itineraryList.length > 0 && {
        genTabTitle: "Itinerary",
        tabComponent: (
          <Itinerary
            itineraryDetails={itineraryDetails}
            itineraryList={itineraryList}
            setItineraryOpenModal={setItineraryOpenModal}
            groupProgram={groupProgramTypes}
            programItineraryFile={programItineraryFile}
          />
        )
      },

    coreGroupsInclusions && {
      genTabTitle: "What's Included",
      tabComponent: (
        <WhatsIncluded
          groupsWhatsIncluded={coreGroupsInclusions}
          groupsWhatsExcluded={coreGroupsExclusions}
        />
      )
    },

    parentInfoDetail && {
      genTabTitle: `${parentInfoDetail.introLabel}`,
      tabComponent: <ParentInfo parentInfo={parentInfoDetail} />
    },

    supportSafety && {
      genTabTitle: "Support & Safety",
      tabComponent: <SupportSafety supportDetails={supportSafety} />
    },
    testimonials &&
      testimonials?.length > 0 && {
        genTabTitle: "Reviews",
        tabComponent: (
          <Testimonials
            testimonials={testimonials}
            groupProgram={groupProgramTypes[0]}
          />
        )
      },

    yourImpactDetails && {
      genTabTitle: `${yourImpactDetails?.yourImpactTitle}`,
      tabComponent: (
        <YourImpact yourImpact={yourImpactDetails} locations={locationPages} />
      )
    },

    ourEthicsDetails && {
      genTabTitle: `${ourEthicsDetails.ourEthicsHeading}`,
      tabComponent: <OurEthics ourEthics={ourEthicsDetails} />
    },

    {
      genTabTitle: "Program ethics",
      tabComponent: (
        <ProgramEthics
          ethics={programEthicsDetails}
          focuses={programDetails?.programFocuses}
        />
      )
    },

    whatitslike && {
      genTabTitle: `${whatitslike.whatsitlikeLabel}`,
      tabComponent: (
        <WhatsItLike
          whatItsLike={whatitslike}
          setAlumniOpenModal={setAlumniOpenModal}
        />
      )
    }
  ].filter((trimEmpty) => trimEmpty);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTab, setIsTab] = useState(false);

  const tabs = useMemo(
    () =>
      tabsDetails && tabsDetails?.length
        ? tabsDetails?.map((tab, index) => ({
            title: tab?.genTabTitle,
            id: index,
            comp: tab?.tabComponent,
            tabState: tab?.tabState,
            accordionSlug: convertToSlug(tab?.genTabTitle),
            accordionState: tab?.tabState,
            ref: createRef()
          }))
        : [],
    [tabsDetails]
  );
  return (
    <>
      <DesktopTabletOnly>
        <GroupProgramViewTabs
          tabs={tabs}
          currentSlide={currentSlide}
          setActiveTabSlidebySlug={setActiveTabSlidebySlug}
          getActiveTabSlidebySlug={getActiveTabSlidebySlug}
          setCurrentSlide={setCurrentSlide}
          setIsTab={setIsTab}
          isTab={isTab}
        />
      </DesktopTabletOnly>
      <MobileOnly>
        <GroupProgramAccordion
          accordions={tabs}
          getActiveTabSlidebySlug={getActiveTabSlidebySlug}
          setActiveTabSlidebySlug={setActiveTabSlidebySlug}
        />
      </MobileOnly>
    </>
  );
};

export default GroupProgramTabs;
